import { Link } from "react-router-dom";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import PageHeader from "@/components/PageHeader";
import RequireCompany from "@/components/RequireCompany";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Scale, TrendingUp, BookOpen, ScrollText, Receipt, AlertOctagon, Layers, RefreshCw, FileBarChart2, ClipboardList, Activity, Boxes } from "lucide-react";

const reports = [
  { to: "all-ledgers", title: "All Ledgers (Detailed)", desc: "Every ledger with full transaction history", icon: Layers },
  { to: "trial-balance", title: "Trial Balance", desc: "All ledgers with closing balances", icon: Scale },
  { to: "profit-loss", title: "Profit & Loss", desc: "Income vs expenses with ledger-wise detail", icon: TrendingUp },
  { to: "balance-sheet", title: "Balance Sheet", desc: "Assets, liabilities and equity (Schedule III)", icon: BookOpen },
  { to: "cash-flow", title: "Cash Flow Statement", desc: "Indirect method — Operating / Investing / Financing (AS-3)", icon: Activity },
  { to: "stock-summary", title: "Inventory Stock Summary", desc: "Per-item opening / purchase / sales / closing with GST", icon: Boxes },
  { to: "item-ledger", title: "Item Ledger (Stock)", desc: "Date-wise transactions for a single stock item with running balance", icon: Layers },
  { to: "day-book", title: "Day Book", desc: "All vouchers grouped by date", icon: ScrollText },
  { to: "gst-summary", title: "GST Summary", desc: "Input vs output CGST/SGST/IGST", icon: Receipt },
  { to: "gstr1", title: "GSTR-1", desc: "Outward supplies — B2B / B2CS / CDN / HSN with JSON + Excel export", icon: FileBarChart2 },
  { to: "gstr3b", title: "GSTR-3B", desc: "Summary return — 3.1, 3.2, 4 (ITC), 6.1 payment", icon: ClipboardList },
  { to: "outstanding", title: "Outstanding", desc: "Receivables and payables ageing", icon: AlertOctagon },
];

function Inner() {
  const { activeId } = useCompany();
  const [busy, setBusy] = useState(false);

  const onRepost = async () => {
    if (!window.confirm(
      "Recompute ledger postings for all existing Sales / Purchase / Debit Note / Credit Note vouchers?\n\n" +
      "This regenerates the standard double-entry postings (Sales Account, Purchase Account, Output/Input GST, etc.) " +
      "for vouchers that were created before auto-posting was enabled. Safe to run multiple times."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/repost-vouchers`);
      toast.success(`Reposted ${data.reposted} of ${data.scanned} inventory vouchers`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Repost failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Financial statements and statutory reports"
        actions={
          <Button variant="outline" onClick={onRepost} disabled={busy} className="h-8" data-testid="btn-repost-vouchers">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Reposting…" : "Repost Sales/Purchase"}
          </Button>
        }
      />
      <div className="p-6 space-y-4">
        <div className="border border-blue-200 bg-blue-50 rounded p-3 text-xs text-blue-900 flex items-start gap-2">
          <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            <strong>Tip:</strong> If you upgraded the software, click <em>Repost Sales/Purchase</em> once to backfill
            ledger postings for older Sales / Purchase / Debit Note / Credit Note vouchers. New vouchers post
            automatically — no need to repeat.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
          {reports.map(({ to, title, desc, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              data-testid={`report-link-${to}`}
              className="border border-gray-200 bg-white rounded p-4 hover:border-blue-700 hover:shadow-sm transition"
            >
              <div className="w-9 h-9 rounded bg-blue-50 text-blue-700 flex items-center justify-center mb-3">
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="font-medium text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500 mt-1">{desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

export default function Reports() {
  return <RequireCompany><Inner /></RequireCompany>;
}
